import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, MapPin, Calendar, LogOut, AlertCircle, CheckCircle, FileText, Users, RefreshCw, ChevronRight, BadgeCheck, Clock3, BookOpen, TrendingUp } from 'lucide-react';
import api from '../api';
import logoutManager from '../logout';

const getDisplayName = (subject) => subject?.subjectName || subject?.name || 'Assigned Subject';

const getSubjectCode = (subject) => subject?.subjectCode || subject?.code || 'N/A';

const getSubjectContextLines = (subject) => {
  const lines = [];
  const programType = subject?.programTypeName ? `${subject.programTypeName}${subject.programTypeCode ? ` (${subject.programTypeCode})` : ''}` : '';
  const program = subject?.programName ? `${subject.programName}${subject.programCode ? ` (${subject.programCode})` : ''}` : '';
  const academicYear = subject?.academicYearName ? `${subject.academicYearName}${subject.academicYearLabel ? ` (${subject.academicYearLabel})` : ''}` : '';

  if (programType) lines.push(`Program Type: ${programType}`);
  if (program) lines.push(`Program: ${program}`);
  if (academicYear) lines.push(`Academic Year: ${academicYear}`);

  if (subject?.isSemesterBased) {
    const semester = subject?.semesterName ? `${subject.semesterName}${subject.semesterNumber ? ` (Sem ${subject.semesterNumber})` : ''}` : '';
    if (semester) lines.push(`Semester: ${semester}`);
  } else {
    const term = subject?.termLabel ? `${subject.termLabel}${subject.termNumber ? ` (Term ${subject.termNumber})` : ''}` : '';
    if (term) lines.push(`Term: ${term}`);
  }

  return lines;
};

const getPacketSummary = (subject) => subject?.packetSummary || {};

const getPercent = (completed = 0, total = 0) => {
  if (!total) return 0;
  return Math.round((Number(completed) / Number(total)) * 100);
};

const getSubjectProgressMeta = (subject) => {
  const packetSummary = getPacketSummary(subject);
  const assignedCount = Number(subject?.studentCount || 0);
  const completedCount = Number(subject?.completedCount || 0);

  const packetTotal = Number(packetSummary.totalPackets || 0);
  const packetCompleted = Number(packetSummary.completedPackets || 0);
  const packetInProgress = Number(packetSummary.inProgressPackets || 0);
  const packetPending = Number(packetSummary.pendingPackets || 0);
  const packetProgress = Number(subject?.packetProgress ?? packetSummary.packetProgress ?? 0);

  const studentTotal = Number(packetSummary.totalStudents || assignedCount || 0);
  const studentCompleted = Number(packetSummary.completedStudents || completedCount || 0);
  const studentPending = Math.max(studentTotal - studentCompleted, 0);

  const studentProgress = getPercent(studentCompleted, studentTotal);

  const progress = packetProgress > 0
    ? packetProgress
    : studentProgress > 0
      ? studentProgress
      : getPercent(completedCount, assignedCount);

  return {
    packetTotal,
    packetCompleted,
    packetPending,
    packetInProgress,
    studentTotal,
    studentCompleted,
    studentPending,
    packetProgress,
    studentProgress,
    progress
  };
};

const EvaluatorDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const tenantId = localStorage.getItem('tenantId');
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No token found, redirecting to login');
        navigate('/signin');
        return;
      }
      
      const response = await api.get('/api/evaluator/dashboard', {
        headers: { 
          'x-tenant-id': tenantId,
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const { data } = response.data;
        setDashboardData(data);
        setAssignedSubjects(data.assignedSubjects || []);
        
        // Store evaluator info in localStorage for persistence
        const evaluatorInfo = {
          remainingDailyHours: data.accessInfo?.remainingDailyHours,
          accessEndsAt: data.accessInfo?.accessEndsAt,
          dailyLimit: data.accessInfo?.dailyLimit,
          minutesUsedToday: data.accessInfo?.minutesUsedToday
        };
        localStorage.setItem('evaluatorInfo', JSON.stringify(evaluatorInfo));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        showStatus('error', 'Session expired. Please login again.');
        setTimeout(() => {
          localStorage.clear();
          navigate('/signin');
        }, 2000);
      } else {
        showStatus('error', error.response?.data?.message || 'Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    }

    // Fetch dashboard data
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    showStatus('success', 'Dashboard refreshed');
  };

  const handleLogout = async () => {
    await logoutManager.logout({
      redirectTo: '/',
      callbacks: false
    });
  };

  const handleMarkEntry = (subject) => {
    navigate('/evaluator/workspace', { 
      state: { 
        subject: {
          ...subject,
          maxMarks: subject.maxMarks || 100
        }
      } 
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const formatTimeRemaining = (hours) => {
    if (!hours && hours !== 0) return '8.0';
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (minutes > 0) {
      return `${wholeHours}.${minutes.toString().padStart(2, '0')}`;
    }
    return `${wholeHours}.0`;
  };

  const showStatus = (type, message) => {
    setStatusMessage({ type, message });
    setTimeout(() => setStatusMessage({ type: '', message: '' }), 5000);
  };

  // Get remaining hours from dashboard data or localStorage
  const getRemainingHours = () => {
    if (dashboardData?.accessInfo?.remainingDailyHours !== undefined) {
      return dashboardData.accessInfo.remainingDailyHours;
    }
    const storedInfo = localStorage.getItem('evaluatorInfo');
    if (storedInfo) {
      const info = JSON.parse(storedInfo);
      return info.remainingDailyHours || 8;
    }
    return 8;
  };

  const getAccessEndsAt = () => {
    if (dashboardData?.accessInfo?.accessEndsAt) {
      return dashboardData.accessInfo.accessEndsAt;
    }
    const storedInfo = localStorage.getItem('evaluatorInfo');
    if (storedInfo) {
      const info = JSON.parse(storedInfo);
      return info.accessEndsAt;
    }
    return null;
  };

  const getDailyLimit = () => {
    if (dashboardData?.accessInfo?.dailyLimit) {
      return dashboardData.accessInfo.dailyLimit;
    }
    return 8;
  };

  const getGeofenceStatus = () => {
    return dashboardData?.accessInfo?.geofenceEnabled || false;
  };

  const sortedSubjects = useMemo(() => {
    return [...assignedSubjects].sort((a, b) => {
      const packetA = getSubjectProgressMeta(a);
      const packetB = getSubjectProgressMeta(b);
      const priorityA = packetA.packetPending > 0 || packetA.studentPending > 0 ? 0 : 1;
      const priorityB = packetB.packetPending > 0 || packetB.studentPending > 0 ? 0 : 1;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return getDisplayName(a).localeCompare(getDisplayName(b), undefined, { sensitivity: 'base' });
    });
  }, [assignedSubjects]);

  const dashboardTotals = useMemo(() => {
    return sortedSubjects.reduce((acc, subject) => {
      const summary = getSubjectProgressMeta(subject);
      acc.totalPackets += summary.packetTotal || 0;
      acc.pendingPackets += summary.packetPending || 0;
      acc.inProgressPackets += summary.packetInProgress || 0;
      acc.completedPackets += summary.packetCompleted || 0;
      acc.totalStudents += summary.studentTotal || 0;
      acc.completedStudents += summary.studentCompleted || 0;
      return acc;
    }, {
      totalPackets: 0,
      pendingPackets: 0,
      inProgressPackets: 0,
      completedPackets: 0,
      totalStudents: 0,
      completedStudents: 0
    });
  }, [sortedSubjects]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const remainingHours = getRemainingHours();
  const accessEndsAt = getAccessEndsAt();
  const dailyLimit = getDailyLimit();
  const geofenceEnabled = getGeofenceStatus();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-xl">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Evaluator Dashboard</h1>
                <p className="text-sm text-slate-500">End Examination Marking Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-slate-500 hover:text-indigo-600 transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <div className="text-right">
                <p className="text-sm font-medium text-slate-800">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-500 hover:text-red-600 transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Message */}
        {statusMessage.message && (
          <div className={`mb-6 rounded-xl border p-4 ${
            statusMessage.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
            'bg-emerald-50 border-emerald-200 text-emerald-800'
          }`}>
            <div className="flex items-center gap-2">
              {statusMessage.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
              <p className="text-sm font-medium">{statusMessage.message}</p>
            </div>
          </div>
        )}

        {/* Access Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="font-semibold text-slate-700">Today's Remaining Time</h3>
            </div>
            <p className="text-2xl font-bold text-slate-800">
              {formatTimeRemaining(remainingHours)} hours
            </p>
            <p className="text-xs text-slate-500 mt-1">Daily limit: {dailyLimit} hours</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-700">Access Expires</h3>
            </div>
            <p className="text-sm font-semibold text-slate-800">
              {formatDate(accessEndsAt)}
            </p>
            <p className="text-xs text-slate-500 mt-1">After this date, access will be revoked</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-indigo-600" />
              </div>
              <h3 className="font-semibold text-slate-700">Live Subjects</h3>
            </div>
            <p className="text-2xl font-bold text-slate-800">{dashboardData?.stats?.totalAssignedSubjects || assignedSubjects.length}</p>
            <p className="text-xs text-slate-500 mt-1">Subjects with real packet data</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${geofenceEnabled ? 'bg-green-100' : 'bg-slate-100'}`}>
                <MapPin className={`w-5 h-5 ${geofenceEnabled ? 'text-green-600' : 'text-slate-400'}`} />
              </div>
              <h3 className="font-semibold text-slate-700">Location Restriction</h3>
            </div>
            <p className={`text-lg font-semibold ${geofenceEnabled ? 'text-green-600' : 'text-slate-500'}`}>
              {geofenceEnabled ? 'Enabled' : 'Disabled'}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {geofenceEnabled ? 'You must be within permitted area' : 'No location restrictions'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total packets</p>
                <p className="text-3xl font-black text-slate-900 mt-2">{dashboardTotals.totalPackets}</p>
              </div>
              <div className="p-3 rounded-2xl bg-slate-100 text-slate-500">
                <FileText className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500" style={{ width: `${Math.min((dashboardTotals.completedPackets / Math.max(dashboardTotals.totalPackets, 1)) * 100, 100)}%` }} />
            </div>
            <p className="text-xs text-slate-500 mt-2">{dashboardTotals.completedPackets} completed, {dashboardTotals.pendingPackets} pending</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Students attached</p>
                <p className="text-3xl font-black text-slate-900 mt-2">{dashboardTotals.totalStudents}</p>
              </div>
              <div className="p-3 rounded-2xl bg-emerald-100 text-emerald-600">
                <Users className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 font-medium">
              <BadgeCheck className="w-4 h-4 text-emerald-500" />
              <span>{dashboardTotals.completedStudents} student marks already saved</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Packet completion</p>
                <p className="text-3xl font-black text-slate-900 mt-2">{getPercent(dashboardTotals.completedPackets, dashboardTotals.totalPackets)}%</p>
              </div>
              <div className="p-3 rounded-2xl bg-amber-100 text-amber-600">
                <TrendingUp className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 font-medium">
              <Clock3 className="w-4 h-4 text-amber-500" />
              <span>{dashboardTotals.inProgressPackets} packet(s) currently in progress</span>
            </div>
          </div>
        </div>

        {/* Assigned Subjects Section */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
            <h2 className="text-lg font-bold text-slate-800">Assigned Subjects for Marking</h2>
            <p className="text-sm text-slate-500 mt-0.5">Live packet counts and progress are loaded from the server</p>
          </div>

          <div className="divide-y divide-slate-100">
            {sortedSubjects.length === 0 ? (
              <div className="text-center py-12">
                <div className="p-3 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-500">No subjects assigned yet</p>
                <p className="text-sm text-slate-400 mt-1">Please contact COE for subject assignments</p>
              </div>
            ) : (
              sortedSubjects.map((subject) => {
                const summary = getSubjectProgressMeta(subject);

                return (
                <button
                  key={subject.subjectId || subject.assignmentId || getDisplayName(subject)}
                  onClick={() => handleMarkEntry(subject)}
                  className="w-full text-left p-5 hover:bg-slate-50 transition-colors group"
                >
                  <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-4 items-center">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-slate-800 group-hover:text-indigo-600 transition-colors text-lg">
                          {getDisplayName(subject)}
                        </h3>
                        <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider">
                          {getSubjectCode(subject)}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                        <span>Students: {subject.studentCount || summary.studentTotal || 0}</span>
                        <span>Max Marks: {subject.maxMarks || 100}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
                        {getSubjectContextLines(subject).map((line) => (
                          <span key={line} className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200">
                            {line}
                          </span>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3 text-[11px] font-bold">
                        <span className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                          Packets done: {summary.packetCompleted}/{summary.packetTotal || 0}
                        </span>
                        <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                          Students done: {summary.studentCompleted}/{summary.studentTotal || 0}
                        </span>
                        <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                          Packet pending: {summary.packetPending}
                        </span>
                        <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                          Student pending: {summary.studentPending}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3 xl:text-right">
                      <div className="flex items-center justify-between gap-3 xl:justify-end text-xs font-bold text-slate-500">
                        <span>Packet progress</span>
                        <span>{summary.packetProgress}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500" style={{ width: `${summary.packetProgress}%` }} />
                      </div>
                      <div className="flex items-center justify-between gap-3 xl:justify-end text-xs font-bold text-slate-500">
                        <span>Student progress</span>
                        <span>{summary.studentProgress}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-lime-500" style={{ width: `${summary.studentProgress}%` }} />
                      </div>
                      <div className="flex items-center justify-between gap-2 xl:justify-end">
                        <span className="text-sm text-indigo-600 font-medium">Open real data</span>
                        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-600 transition-colors" />
                      </div>
                    </div>
                  </div>
                </button>
                );
              })
            )}
          </div>
        </div>

        {/* Stats Summary (if subjects exist) */}
        {sortedSubjects.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Total Students to Evaluate</h4>
              <p className="text-2xl font-bold text-indigo-600">
                {dashboardData?.stats?.totalStudents || dashboardTotals.totalStudents}
              </p>
              <p className="text-xs text-slate-500 mt-1">Across all assigned subjects</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Remaining Today</h4>
              <p className="text-2xl font-bold text-amber-600">{formatTimeRemaining(remainingHours)} hours</p>
              <p className="text-xs text-slate-500 mt-1">Of {dailyLimit} hour daily limit</p>
            </div>
          </div>
        )}

        {/* Reminder Section */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-amber-800">Important Notes</h4>
              <ul className="text-xs text-amber-700 mt-1 space-y-1">
                <li>• Your login session will automatically expire after {formatTimeRemaining(remainingHours)} hours of active use today</li>
                <li>• Marks entered are auto-saved every 2 minutes</li>
                {geofenceEnabled && (
                  <li>• Location services must be enabled while marking (geofence is active)</li>
                )}
                <li>• Use the subject cards above to jump into the live packet workspace</li>
                <li>• Access expires on {formatDate(accessEndsAt)}</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EvaluatorDashboard;